import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { keyBy, pick } from 'lodash';
import * as Yup from 'yup';

import { Box, Typography } from '@mui/material';

import {
  PerformanceBasedPayModel,
  PerformanceBasedPayPerformanceMetricsModel,
  PublicUtilityType,
} from 'models';
import { BaseEditorDialogProps, EditorDialog } from 'components';
import LeoTextField from 'components/input-fields/leoTextField/LeoTextField';
import { useEditPerformanceBasedPay } from 'features/performanceBasedPay';

export interface NewPerformanceEditorDialogProps
  extends BaseEditorDialogProps<PerformanceBasedPayModel> {}

const UTILITY_LABEL_KEY: Record<string, string> = {
  [PublicUtilityType.ELECTRICITY]: 'KOZOS_TELJESITMENYBEREK.ELECTRICITY',
  [PublicUtilityType.GAS]: 'KOZOS_TELJESITMENYBEREK.GAS',
};

const getMetricFieldKey = (performanceMetricKey: string, publicUtilityType: string) =>
  `${performanceMetricKey}_${publicUtilityType}`;

const METRIC_KEYS = [
  'CP_VISITED',
  'CP_WITH_METERING_PHOTOS',
  'CUSTOMER_METER_READINGS',
  'DATA_CORRECTIONS',
  'LONG_TIME_NOT_SEEN_ABOVE_50_PERCENT',
  'LONG_TIME_NOT_SEEN_BELOW_50_PERCENT',
  'METER_PHOTOS',
  'MISSED_CR',
  'NOT_DOABLE_CR',
  'NOT_READABLE_PHOTOS',
  'ON_TIME_CR',
  'OTHER_READING_PHOTOS',
  'OTHER_CR_PHOTOS',
  'READ_METER_READINGS',
  'REPORTS',
  'UNHANDLED_ORDERS',
];

interface FormikValues
  extends Omit<PerformanceBasedPayPerformanceMetricsModel, 'count' | 'toBePaid'> {
  count: string | number;
  toBePaid: string | number;
}

const NewPerformanceEditorDialog: FC<NewPerformanceEditorDialogProps> = ({
  selected,
  ...props
}) => {
  const isEdit = Boolean(selected && selected.id);
  const { mutateAsync: editPerformanceBasedPay } = useEditPerformanceBasedPay();

  const otherPerformanceBasedPay =
    selected?.otherPerformanceBasedPay === 0 ? '0' : selected?.otherPerformanceBasedPay;

  const hoursSpentWithOtherActivities =
    selected?.hoursSpentWithOtherActivities === 0 ? '0' : selected?.hoursSpentWithOtherActivities;

  const onSubmit = async (values: Partial<PerformanceBasedPayModel>) => {
    const performanceBasedPayPerformanceMetrics: any = Object.values(
      values.performanceMetricsObject || {}
    )?.map((metric) => {
      metric.count = +metric.count;
      metric.toBePaid = +metric.toBePaid;
      return pick(metric, 'id', 'count', 'toBePaid');
    });

    const sendData = pick(values, 'otherPerformanceBasedPay', 'hoursSpentWithOtherActivities');
    if (sendData.otherPerformanceBasedPay && sendData.hoursSpentWithOtherActivities) {
      sendData.hoursSpentWithOtherActivities = +sendData.hoursSpentWithOtherActivities;
      sendData.otherPerformanceBasedPay = +sendData.otherPerformanceBasedPay;
    }

    if (values.id) {
      await editPerformanceBasedPay({
        id: values.id,
        data: { ...sendData, performanceBasedPayPerformanceMetrics },
      });
    }
  };

  if (selected) {
    selected.performanceMetricsObject = keyBy(
      selected?.performanceBasedPayPerformanceMetrics,
      (metric: PerformanceBasedPayPerformanceMetricsModel) =>
        getMetricFieldKey(metric.performanceMetric.key, metric.publicUtilityType)
    );
  }

  const getMetricFields = () => {
    if (selected && selected.performanceMetricsObject) {
      return Object.values(selected.performanceMetricsObject).map((item: FormikValues) => {
        item.toBePaid = item.toBePaid.toString();
        item.count = item.count.toString();

        const fieldKey = getMetricFieldKey(item.performanceMetric.key, item.publicUtilityType);

        return (
          <Box key={item.id}>
            <Typography variant="subtitle2">
              {item.performanceMetric.value} -{' '}
              <Trans i18nKey={UTILITY_LABEL_KEY[item.publicUtilityType]} />
            </Typography>
            <Box display="flex" columnGap={5}>
              <LeoTextField
                disabled={item.performanceMetric.isAutoCounted}
                name={`performanceMetricsObject.${fieldKey}.count`}
                label={<Trans i18nKey="KOZOS_TELJESITMENYBEREK.COMPLETED" />}
              />
              <LeoTextField
                disabled
                name={`performanceMetricsObject.${fieldKey}.toBePaid`}
                label={<Trans i18nKey="KOZOS_TELJESITMENYBEREK.TO_BE_PAID" />}
              />
            </Box>
          </Box>
        );
      });
    }
    return null;
  };

  const initVal = isEdit
    ? {
        ...selected,
        otherPerformanceBasedPay,
        hoursSpentWithOtherActivities,
      }
    : {};

  return (
    <EditorDialog
      initialValues={{ ...initVal }}
      maxWidth="sm"
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      title={<Trans i18nKey="KOZOS_TELJESITMENYBEREK.EDIT" />}
      {...props}
    >
      <LeoTextField
        name="otherPerformanceBasedPay"
        label={<Trans i18nKey="KOZOS_TELJESITMENYBEREK.OTHER_PBP" />}
      />

      <LeoTextField
        name="hoursSpentWithOtherActivities"
        label={<Trans i18nKey="KOZOS_TELJESITMENYBEREK.HOURS_SPENT_WITH_OTHER" />}
      />
      {getMetricFields()}
    </EditorDialog>
  );
};

export default NewPerformanceEditorDialog;

const metricFieldSchema = Yup.object()
  .notRequired()
  .default(undefined)
  .shape({
    count: Yup.number().required().min(0).max(9999),
    toBePaid: Yup.number().required().min(-999999).max(999999),
  });

const performanceMetricsObjectShape = METRIC_KEYS.reduce<Record<string, typeof metricFieldSchema>>(
  (shape, metricKey) => {
    shape[getMetricFieldKey(metricKey, PublicUtilityType.ELECTRICITY)] = metricFieldSchema;
    shape[getMetricFieldKey(metricKey, PublicUtilityType.GAS)] = metricFieldSchema;
    return shape;
  },
  {}
);

const validationSchema = Yup.object().shape({
  otherPerformanceBasedPay: Yup.number().required().min(-999999).max(999999),
  hoursSpentWithOtherActivities: Yup.number().required().min(0).max(250),
  performanceMetricsObject: Yup.object()
    .notRequired()
    .default(undefined)
    .shape(performanceMetricsObjectShape),
});
